import { CSSProperties, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';


/**
 * FullscreenAssetViewer — shared asset preview used by the house catalog
 * (RealEstateModal) and the Ancient Lighthouse (LighthouseModal).
 *
 * Renders a thumbnail image with a small magnifier (zoom) icon in the corner.
 * Clicking the image OR the magnifier opens the asset full-screen over a dark
 * backdrop; clicking the backdrop, the close button, or pressing the magnifier
 * again closes it. Purely presentational + self-contained open/close state.
 */

interface FullscreenAssetViewerProps {
  src: string;
  alt?: string;
  /** Style applied to the inline thumbnail <img>. */
  thumbnailStyle?: CSSProperties;
  /** Style applied to the wrapping <div> around the thumbnail. */
  wrapperStyle?: CSSProperties;
  /** Extra style merged onto the magnifier badge (e.g. reposition). */
  magnifierStyle?: CSSProperties;
  /** Hide the magnifier badge (image itself still opens fullscreen on click). */
  hideMagnifier?: boolean;
  /** Optional click on the thumbnail before opening (does not block opening). */
  onThumbnailClick?: () => void;
}

function MagnifierIcon({ size = 16, color = 'var(--color-white)' }: { size?: number; color?: string }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <circle cx="7" cy="7" r="4" stroke={color} strokeWidth="1.5" fill="none" />
      <path d="M10 10L13 13" stroke={color} strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

export default function FullscreenAssetViewer({
  src,
  alt = '',
  thumbnailStyle,
  wrapperStyle,
  magnifierStyle,
  hideMagnifier = false,
  onThumbnailClick,
}: FullscreenAssetViewerProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const openFullscreen = () => {
    onThumbnailClick?.();
    setOpen(true);
  };

  return (
    <div className="relative inline-block" style={wrapperStyle}>
      <img
        src={src}
        alt={alt}
        onClick={openFullscreen}
        className="block cursor-zoom-in"
        style={thumbnailStyle}
      />

      {!hideMagnifier && (
        <button
          type="button"
          aria-label={t('Zoom in')}
          onClick={(e) => {
            e.stopPropagation();
            openFullscreen();
          }}
          className="absolute top-[6px] right-[6px] flex h-7 w-7 cursor-pointer items-center justify-center rounded-full border border-solid border-white-alpha-25 bg-asset-viewer-surface-alpha-55 p-0 backdrop-blur-[6px]"
          style={magnifierStyle}
        >
          <MagnifierIcon size={15} />
        </button>
      )}

      {open &&
        createPortal(
          <div
            onClick={() => setOpen(false)}
            className="fixed inset-0 z-[10050] flex cursor-zoom-out items-center justify-center bg-asset-viewer-toolbar-alpha-92 p-2xl"
          >
            <img
              src={src}
              alt={alt}
              onClick={(e) => e.stopPropagation()}
              className="max-h-full max-w-full cursor-default rounded-lg object-contain [filter:drop-shadow(0_18px_50px_var(--color-black-alpha-60))]"
            />
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label={t('Close')}
              className="fixed top-[18px] right-[18px] flex h-10 w-10 cursor-pointer items-center justify-center rounded-full border border-solid border-white-alpha-20 bg-asset-viewer-surface-alpha-60 p-0 text-[22px] leading-none text-white backdrop-blur-[8px]"
            >
              ×
            </button>
          </div>,
          document.body,
        )}
    </div>
  );
}
